import { useState, useCallback, useEffect, useRef } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Modal,
  Switch,
  KeyboardAvoidingView,
  Platform,
  Alert,
  ActivityIndicator,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { logActivity } from "@/lib/activity-log";
import { scheduleEventReminder } from "@/lib/notifications";
import { AgendaEventType, AgendaVisibility, AgendaCategory } from "@/types";
import { format, addHours, endOfDay } from "date-fns";
import { nl } from "date-fns/locale";
import DateTimePicker from "@react-native-community/datetimepicker";
import LottieView from "lottie-react-native";
import { LocationPicker } from "./LocationPicker";
import { ChallengePicker, ChallengeWithStatus } from "@/modules/challenges";
import { getAllLotties, getLottieSource } from "@/lib/lottie-registry";

/**
 * Category options
 */
const CATEGORY_OPTIONS: { value: AgendaCategory; label: string; icon: string }[] = [
  { value: "activity", label: "Activiteit", icon: "game-controller" },
  { value: "meal", label: "Maaltijd", icon: "restaurant" },
  { value: "game", label: "Spel", icon: "trophy" },
  { value: "ceremony", label: "Ceremonie", icon: "ribbon" },
  { value: "free_time", label: "Vrije tijd", icon: "cafe" },
  { value: "custom", label: "Anders", icon: "ellipse" },
];

/**
 * Admin-only categories
 */
const ADMIN_CATEGORY_OPTIONS: { value: AgendaCategory; label: string; icon: string }[] = [
  { value: "family_day", label: "Familie Dag", icon: "sunny" },
  { value: "family_weekend", label: "Familie Weekend", icon: "calendar" },
  ...CATEGORY_OPTIONS,
];

/**
 * Color options for events
 */
const COLOR_OPTIONS = [
  "#7C3AED", // Purple
  "#3B82F6", // Blue
  "#10B981", // Green
  "#F59E0B", // Amber
  "#EF4444", // Red
  "#EC4899", // Pink
  "#6366F1", // Indigo
  "#6B7280", // Gray
];

/**
 * Lottie animation options - uses central registry
 */
const LOTTIE_OPTIONS: { value: string | null; label: string; emoji: string }[] = [
  { value: null, label: "Geen", emoji: "❌" },
  ...getAllLotties()
    .filter((l) => ["celebration", "action", "object"].includes(l.category))
    .map((l) => ({
      value: l.key,
      label: l.name,
      emoji: "🎬",
    })),
];

/**
 * Props for CreateEventModal
 */
interface CreateEventModalProps {
  visible: boolean;
  onClose: () => void;
  onEventCreated: () => void;
  initialDate?: Date;
}

/**
 * CreateEventModal Component
 * Modal for creating new calendar events
 */
export const CreateEventModal = ({
  visible,
  onClose,
  onEventCreated,
  initialDate = new Date(),
}: CreateEventModalProps) => {
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { profile, roles, activeWeekend, userTeam } = useAuth();

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [locationCoordinates, setLocationCoordinates] = useState<{ latitude: number; longitude: number } | undefined>();
  const [category, setCategory] = useState<AgendaCategory>("activity");
  const [color, setColor] = useState(COLOR_OPTIONS[0]);
  
  // Date/time state
  const [startDate, setStartDate] = useState(initialDate);
  const [endDate, setEndDate] = useState(addHours(initialDate, 1));
  const [allDay, setAllDay] = useState(false);
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);
  const [pickerMode, setPickerMode] = useState<"date" | "time">("date");

  // Event type & visibility
  const [eventType, setEventType] = useState<AgendaEventType>("family");
  const [isPrivate, setIsPrivate] = useState(false);
  const [visibility, setVisibility] = useState<AgendaVisibility>("public");

  // Options
  const [isMandatory, setIsMandatory] = useState(false);
  const [sendReminder, setSendReminder] = useState(false);
  const [reminderMinutes, setReminderMinutes] = useState(30);

  // Reveal settings (admin only)
  const [hasReveal, setHasReveal] = useState(false);
  const [revealTarget, setRevealTarget] = useState<"location" | "details" | "event" | "all">("location");
  const [secretTitle, setSecretTitle] = useState("");
  const [secretDescription, setSecretDescription] = useState("");
  const [revealAt, setRevealAt] = useState<Date | null>(null);
  const [showRevealDatePicker, setShowRevealDatePicker] = useState(false);

  // Linked challenges (admin only)
  const [linkedChallenges, setLinkedChallenges] = useState<Array<{ challenge: ChallengeWithStatus; revealTargets: string[] }>>([]);
  const [showChallengePicker, setShowChallengePicker] = useState(false);

  // Event audience
  const [eventAudience, setEventAudience] = useState<"personal" | "team" | "everyone">("everyone");

  // Lottie animation
  const [lottieAnimation, setLottieAnimation] = useState<string | null>(null);

  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isAdmin = roles.isFamilieAdmin || roles.isSuperAdmin;

  /**
   * Reset form when modal opens/closes
   */
  useEffect(() => {
    if (visible) {
      setTitle("");
      setDescription("");
      setLocation("");
      setLocationCoordinates(undefined);
      setCategory("activity");
      setColor(COLOR_OPTIONS[0]);
      setStartDate(initialDate);
      setEndDate(addHours(initialDate, 1));
      setAllDay(false);
      setEventType("family");
      setIsPrivate(false);
      setVisibility("public");
      setIsMandatory(false);
      setSendReminder(false);
      setHasReveal(false);
      setRevealTarget("location");
      setSecretTitle("");
      setSecretDescription("");
      setRevealAt(null);
      setLinkedChallenges([]);
      setEventAudience("everyone");
      setLottieAnimation(null);
    }
  }, [visible, initialDate, isAdmin]);

  /**
   * Handle private toggle
   */
  const handlePrivateToggle = useCallback((value: boolean) => {
    setIsPrivate(value);
    if (value) {
      setEventType("private");
      setVisibility("team_only");
    } else {
      setEventType("family");
      setVisibility("public");
    }
  }, []);

  /**
   * Handle start date change
   */
  const handleStartDateChange = useCallback((event: any, date?: Date) => {
    if (Platform.OS === "android") {
      setShowStartPicker(false);
    }
    if (date) {
      setStartDate(date);
      // Adjust end date if it's before start date
      if (date >= endDate) {
        setEndDate(addHours(date, 1));
      }
    }
  }, [endDate]);

  /**
   * Handle end date change
   */
  const handleEndDateChange = useCallback((event: any, date?: Date) => {
    if (Platform.OS === "android") {
      setShowEndPicker(false);
    }
    if (date) {
      setEndDate(date);
    }
  }, []);

  /**
   * Open date picker with mode
   */
  const openStartPicker = (mode: "date" | "time") => {
    setPickerMode(mode);
    setShowStartPicker(true);
  };

  const openEndPicker = (mode: "date" | "time") => {
    setPickerMode(mode);
    setShowEndPicker(true);
  };

  /**
   * Submit event
   */
  const handleSubmit = useCallback(async () => {
    // Validation
    if (!title.trim()) {
      Alert.alert("Fout", "Vul een titel in");
      return;
    }

    if (!profile?.id) {
      Alert.alert("Fout", "Je bent niet ingelogd");
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare event data (matching actual database columns)
      const eventData = {
        weekend_id: activeWeekend?.id || null,
        title: title.trim(),
        description: description.trim() || null,
        location: location.trim() || null,
        location_lat: locationCoordinates?.latitude || null,
        location_lng: locationCoordinates?.longitude || null,
        starts_at: startDate.toISOString(),
        ends_at: allDay ? endOfDay(startDate).toISOString() : endDate.toISOString(),
        all_day: allDay,
        category,
        color,
        is_mandatory: isMandatory,
        send_reminder: sendReminder,
        reminder_minutes_before: reminderMinutes,
        created_by: profile.id,
        event_type: eventType,
        visibility,
        team_id: isPrivate && userTeam ? userTeam.id : null,
        has_reveal: hasReveal,
        is_revealed: !hasReveal,
        reveal_target: hasReveal ? revealTarget : null,
        reveal_at: hasReveal && revealAt ? revealAt.toISOString() : null,
        secret_title: hasReveal && secretTitle.trim() ? secretTitle.trim() : null,
        secret_description: hasReveal && secretDescription.trim() ? secretDescription.trim() : null,
        // Notification columns
        notify_on_create: sendReminder,
        notify_on_reveal: hasReveal,
        lottie_animation: lottieAnimation,
      };

      const { data: insertedEvent, error } = await supabase
        .from("agenda_events")
        .insert(eventData)
        .select("id")
        .single();

      if (error) {
        throw error;
      }

      // Log the activity
      await logActivity({
        user_id: profile.id,
        action_type: "agenda.create",
        action_category: "agenda",
        target_type: "agenda_event",
        target_id: insertedEvent?.id,
        target_name: title.trim(),
        metadata: {
          event_type: eventType,
          visibility,
          category,
          is_private: isPrivate,
          has_reveal: hasReveal,
        },
        weekend_id: activeWeekend?.id || undefined,
      });

      // Schedule reminder notification if enabled
      if (sendReminder && insertedEvent?.id) {
        await scheduleEventReminder({
          eventId: insertedEvent.id,
          eventTitle: title.trim(),
          eventStartsAt: startDate.toISOString(),
          minutesBefore: reminderMinutes,
          userId: profile.id,
        });
      }

      Alert.alert("Succes", "Evenement aangemaakt!", [
        {
          text: "OK",
          onPress: () => {
            onEventCreated();
            onClose();
          },
        },
      ]);
    } catch (error: any) {
      console.error("Error creating event:", error);
      Alert.alert("Fout", error.message || "Kon evenement niet aanmaken");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    title,
    description,
    location,
    locationCoordinates,
    startDate,
    endDate,
    allDay,
    category,
    color,
    isMandatory,
    sendReminder,
    reminderMinutes,
    eventType,
    visibility,
    isPrivate,
    hasReveal,
    revealTarget,
    revealAt,
    secretTitle,
    secretDescription,
    lottieAnimation,
    profile,
    activeWeekend,
    userTeam,
    onEventCreated,
    onClose,
  ]);

  const categoryOptions = isAdmin ? ADMIN_CATEGORY_OPTIONS : CATEGORY_OPTIONS;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          className="flex-1"
        >
          {/* Header */}
          <View
            className="flex-row items-center justify-between px-4 py-3 border-b"
            style={{ borderColor: colors.border }}
          >
          <TouchableOpacity onPress={onClose} accessibilityLabel="Sluiten">
            <Text style={{ color: colors.primary }}>Annuleer</Text>
          </TouchableOpacity>
          <Text className="text-lg font-bold" style={{ color: colors.text }}>
            Nieuw Evenement
          </Text>
          <TouchableOpacity
            onPress={handleSubmit}
            disabled={isSubmitting}
            accessibilityLabel="Opslaan"
          >
            {isSubmitting ? (
              <ActivityIndicator size="small" color={colors.primary} />
            ) : (
              <Text className="font-semibold" style={{ color: colors.primary }}>
                Opslaan
              </Text>
            )}
          </TouchableOpacity>
        </View>

        <ScrollView className="flex-1 px-4" showsVerticalScrollIndicator={false}>
          {/* Title */}
          <View className="mt-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Titel *
            </Text>
            <TextInput
              value={title}
              onChangeText={setTitle}
              placeholder="Naam van het evenement"
              placeholderTextColor={colors.textTertiary}
              className="px-4 py-3 rounded-xl"
              style={{
                backgroundColor: colors.surfaceSecondary,
                color: colors.text,
              }}
              accessibilityLabel="Titel invoer"
            />
          </View>

          {/* Visibility Selector */}
          <View className="mt-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Zichtbaarheid
            </Text>
            <View className="flex-row">
              {/* Private - only me */}
              <TouchableOpacity
                onPress={() => {
                  setEventType("private");
                  setVisibility("team_only");
                  setIsPrivate(true);
                }}
                className="flex-1 mr-1 px-2 py-3 rounded-xl items-center"
                style={{
                  backgroundColor: eventType === "private" && visibility === "team_only" && isPrivate
                    ? colors.primary
                    : colors.surfaceSecondary,
                }}
              >
                <Ionicons
                  name="lock-closed"
                  size={22}
                  color={eventType === "private" && isPrivate ? "#FFFFFF" : colors.textSecondary}
                />
                <Text
                  className="text-xs mt-1 font-medium text-center"
                  style={{
                    color: eventType === "private" && isPrivate ? "#FFFFFF" : colors.text,
                  }}
                >
                  Privé
                </Text>
                <Text
                  className="text-[10px] text-center"
                  style={{
                    color: eventType === "private" && isPrivate ? "#FFFFFF99" : colors.textTertiary,
                  }}
                >
                  Alleen jij
                </Text>
              </TouchableOpacity>

              {/* Team only */}
              <TouchableOpacity
                onPress={() => {
                  setEventType("family");
                  setVisibility("team_only");
                  setIsPrivate(false);
                }}
                className="flex-1 mx-1 px-2 py-3 rounded-xl items-center"
                style={{
                  backgroundColor: eventType === "family" && visibility === "team_only"
                    ? colors.primary
                    : colors.surfaceSecondary,
                }}
              >
                <Ionicons
                  name="people"
                  size={22}
                  color={eventType === "family" && visibility === "team_only" ? "#FFFFFF" : colors.textSecondary}
                />
                <Text
                  className="text-xs mt-1 font-medium text-center"
                  style={{
                    color: eventType === "family" && visibility === "team_only" ? "#FFFFFF" : colors.text,
                  }}
                >
                  Team
                </Text>
                <Text
                  className="text-[10px] text-center"
                  style={{
                    color: eventType === "family" && visibility === "team_only" ? "#FFFFFF99" : colors.textTertiary,
                  }}
                >
                  Jouw team
                </Text>
              </TouchableOpacity>

              {/* Public - everyone */}
              <TouchableOpacity
                onPress={() => {
                  setEventType("family");
                  setVisibility("public");
                  setIsPrivate(false);
                }}
                className="flex-1 ml-1 px-2 py-3 rounded-xl items-center"
                style={{
                  backgroundColor: eventType === "family" && visibility === "public"
                    ? colors.primary
                    : colors.surfaceSecondary,
                }}
              >
                <Ionicons
                  name="globe"
                  size={22}
                  color={eventType === "family" && visibility === "public" ? "#FFFFFF" : colors.textSecondary}
                />
                <Text
                  className="text-xs mt-1 font-medium text-center"
                  style={{
                    color: eventType === "family" && visibility === "public" ? "#FFFFFF" : colors.text,
                  }}
                >
                  Iedereen
                </Text>
                <Text
                  className="text-[10px] text-center"
                  style={{
                    color: eventType === "family" && visibility === "public" ? "#FFFFFF99" : colors.textTertiary,
                  }}
                >
                  Alle familie
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Category Selector */}
          <View className="mt-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Categorie
            </Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View className="flex-row">
                {categoryOptions.map((cat) => (
                  <TouchableOpacity
                    key={cat.value}
                    onPress={() => setCategory(cat.value)}
                    className="mr-2 px-4 py-2 rounded-full flex-row items-center"
                    style={{
                      backgroundColor:
                        category === cat.value ? colors.primary : colors.surfaceSecondary,
                    }}
                  >
                    <Ionicons
                      name={cat.icon as any}
                      size={16}
                      color={category === cat.value ? "#FFFFFF" : colors.textSecondary}
                    />
                    <Text
                      className="ml-2 text-sm font-medium"
                      style={{
                        color: category === cat.value ? "#FFFFFF" : colors.text,
                      }}
                    >
                      {cat.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
          </View>

          {/* Color Selector */}
          <View className="mt-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Kleur
            </Text>
            <View className="flex-row">
              {COLOR_OPTIONS.map((c) => (
                <TouchableOpacity
                  key={c}
                  onPress={() => setColor(c)}
                  className="w-10 h-10 rounded-full mr-2 items-center justify-center"
                  style={{
                    backgroundColor: c,
                    borderWidth: color === c ? 3 : 0,
                    borderColor: colors.text,
                  }}
                >
                  {color === c && <Ionicons name="checkmark" size={20} color="#FFFFFF" />}
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Lottie Animation Selector */}
          <View className="mt-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Animatie (optioneel)
            </Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View className="flex-row">
                {LOTTIE_OPTIONS.map((lottie) => (
                  <TouchableOpacity
                    key={lottie.value || "none"}
                    onPress={() => setLottieAnimation(lottie.value)}
                    className="mr-2 px-4 py-2 rounded-full flex-row items-center"
                    style={{
                      backgroundColor:
                        lottieAnimation === lottie.value ? colors.primary : colors.surfaceSecondary,
                    }}
                  >
                    <Text className="text-base mr-1">{lottie.emoji}</Text>
                    <Text
                      className="text-sm font-medium"
                      style={{
                        color: lottieAnimation === lottie.value ? "#FFFFFF" : colors.text,
                      }}
                    >
                      {lottie.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>

            {/* Animation Preview */}
            {lottieAnimation && getLottieSource(lottieAnimation) && (
              <View
                className="mt-4 rounded-xl items-center justify-center py-4"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                  Preview
                </Text>
                <LottieView
                  source={getLottieSource(lottieAnimation)}
                  autoPlay
                  loop
                  style={{ width: 150, height: 150 }}
                />
                <Text className="text-xs mt-2" style={{ color: colors.textTertiary }}>
                  {LOTTIE_OPTIONS.find((l) => l.value === lottieAnimation)?.label}
                </Text>
              </View>
            )}
          </View>

          {/* All Day Toggle */}
          <View
            className="flex-row items-center justify-between mt-4 px-4 py-3 rounded-xl"
            style={{ backgroundColor: colors.surfaceSecondary }}
          >
            <View className="flex-row items-center">
              <Ionicons name="sunny" size={20} color={colors.primary} />
              <Text className="ml-3 font-medium" style={{ color: colors.text }}>
                Hele dag
              </Text>
            </View>
            <Switch
              value={allDay}
              onValueChange={setAllDay}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor="#FFFFFF"
            />
          </View>

          {/* Date & Time */}
          <View className="mt-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Datum & Tijd
            </Text>

            {/* Start */}
            <View
              className="flex-row items-center mb-2 px-4 py-3 rounded-xl"
              style={{ backgroundColor: colors.surfaceSecondary }}
            >
              <Ionicons name="play-circle" size={20} color={colors.success} />
              <Text className="ml-3 flex-1" style={{ color: colors.textSecondary }}>
                Start
              </Text>
              <TouchableOpacity
                onPress={() => openStartPicker("date")}
                className="px-3 py-1.5 rounded-lg mr-2"
                style={{ backgroundColor: colors.background }}
              >
                <Text style={{ color: colors.text }}>
                  {format(startDate, "d MMM yyyy", { locale: nl })}
                </Text>
              </TouchableOpacity>
              {!allDay && (
                <TouchableOpacity
                  onPress={() => openStartPicker("time")}
                  className="px-3 py-1.5 rounded-lg"
                  style={{ backgroundColor: colors.background }}
                >
                  <Text style={{ color: colors.text }}>
                    {format(startDate, "HH:mm")}
                  </Text>
                </TouchableOpacity>
              )}
            </View>

            {/* End */}
            {!allDay && (
              <View
                className="flex-row items-center px-4 py-3 rounded-xl"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Ionicons name="stop-circle" size={20} color={colors.error} />
                <Text className="ml-3 flex-1" style={{ color: colors.textSecondary }}>
                  Einde
                </Text>
                <TouchableOpacity
                  onPress={() => openEndPicker("date")}
                  className="px-3 py-1.5 rounded-lg mr-2"
                  style={{ backgroundColor: colors.background }}
                >
                  <Text style={{ color: colors.text }}>
                    {format(endDate, "d MMM yyyy", { locale: nl })}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => openEndPicker("time")}
                  className="px-3 py-1.5 rounded-lg"
                  style={{ backgroundColor: colors.background }}
                >
                  <Text style={{ color: colors.text }}>
                    {format(endDate, "HH:mm")}
                  </Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Location */}
          <View className="mt-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Locatie
            </Text>
            <LocationPicker
              value={location}
              onChange={(newLocation, coordinates) => {
                setLocation(newLocation);
                setLocationCoordinates(coordinates);
              }}
              placeholder="Voeg locatie toe"
            />
          </View>

          {/* Description */}
          <View className="mt-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Beschrijving
            </Text>
            <TextInput
              value={description}
              onChangeText={setDescription}
              placeholder="Voeg beschrijving toe"
              placeholderTextColor={colors.textTertiary}
              multiline
              numberOfLines={4}
              className="px-4 py-3 rounded-xl"
              style={{
                backgroundColor: colors.surfaceSecondary,
                color: colors.text,
                minHeight: 100,
                textAlignVertical: "top",
              }}
            />
          </View>

          {/* Reminder Toggle */}
          <View
            className="flex-row items-center justify-between mt-4 px-4 py-3 rounded-xl"
            style={{ backgroundColor: colors.surfaceSecondary }}
          >
            <View className="flex-row items-center">
              <Ionicons name="notifications" size={20} color={colors.primary} />
              <Text className="ml-3 font-medium" style={{ color: colors.text }}>
                Herinnering sturen
              </Text>
            </View>
            <Switch
              value={sendReminder}
              onValueChange={setSendReminder}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor="#FFFFFF"
            />
          </View>

          {/* Admin-only options */}
          {isAdmin && !isPrivate && (
            <>
              {/* Event Audience */}
              <View className="mt-4">
                <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                  Voor wie is dit evenement?
                </Text>
                <View className="flex-row">
                  <TouchableOpacity
                    onPress={() => setEventAudience("personal")}
                    className="flex-1 mr-1 px-3 py-3 rounded-xl items-center"
                    style={{
                      backgroundColor: eventAudience === "personal" ? colors.primary : colors.surfaceSecondary,
                    }}
                  >
                    <Ionicons
                      name="person"
                      size={20}
                      color={eventAudience === "personal" ? "#FFFFFF" : colors.textSecondary}
                    />
                    <Text
                      className="text-xs mt-1 font-medium"
                      style={{ color: eventAudience === "personal" ? "#FFFFFF" : colors.text }}
                    >
                      Alleen mij
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => setEventAudience("team")}
                    className="flex-1 mx-1 px-3 py-3 rounded-xl items-center"
                    style={{
                      backgroundColor: eventAudience === "team" ? colors.primary : colors.surfaceSecondary,
                    }}
                  >
                    <Ionicons
                      name="people"
                      size={20}
                      color={eventAudience === "team" ? "#FFFFFF" : colors.textSecondary}
                    />
                    <Text
                      className="text-xs mt-1 font-medium"
                      style={{ color: eventAudience === "team" ? "#FFFFFF" : colors.text }}
                    >
                      Mijn team
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => setEventAudience("everyone")}
                    className="flex-1 ml-1 px-3 py-3 rounded-xl items-center"
                    style={{
                      backgroundColor: eventAudience === "everyone" ? colors.primary : colors.surfaceSecondary,
                    }}
                  >
                    <Ionicons
                      name="globe"
                      size={20}
                      color={eventAudience === "everyone" ? "#FFFFFF" : colors.textSecondary}
                    />
                    <Text
                      className="text-xs mt-1 font-medium"
                      style={{ color: eventAudience === "everyone" ? "#FFFFFF" : colors.text }}
                    >
                      Iedereen
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>

              {/* Mandatory Toggle */}
              <View
                className="flex-row items-center justify-between mt-4 px-4 py-3 rounded-xl"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <View className="flex-row items-center">
                  <Ionicons name="alert-circle" size={20} color={colors.warning} />
                  <View className="ml-3">
                    <Text className="font-medium" style={{ color: colors.text }}>
                      Verplicht evenement
                    </Text>
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>
                      Markeer als verplichte aanwezigheid
                    </Text>
                  </View>
                </View>
                <Switch
                  value={isMandatory}
                  onValueChange={setIsMandatory}
                  trackColor={{ false: colors.border, true: colors.warning }}
                  thumbColor="#FFFFFF"
                />
              </View>

              {/* Reveal Toggle */}
              <View
                className="flex-row items-center justify-between mt-4 px-4 py-3 rounded-xl"
                style={{
                  backgroundColor: colors.surfaceSecondary,
                  borderWidth: hasReveal ? 1 : 0,
                  borderColor: colors.primary,
                }}
              >
                <View className="flex-row items-center">
                  <Ionicons name="eye-off" size={20} color={colors.primary} />
                  <View className="ml-3">
                    <Text className="font-medium" style={{ color: colors.text }}>
                      Onthulling activeren
                    </Text>
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>
                      Verberg details tot onthulling
                    </Text>
                  </View>
                </View>
                <Switch
                  value={hasReveal}
                  onValueChange={setHasReveal}
                  trackColor={{ false: colors.border, true: colors.primary }}
                  thumbColor="#FFFFFF"
                />
              </View>

              {hasReveal && (
                <View className="mt-4">
                  {/* What to hide */}
                  <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                    Wat wil je verbergen?
                  </Text>
                  <View className="flex-row flex-wrap mb-4">
                    {[
                      { value: "location", label: "Locatie", icon: "location" },
                      { value: "details", label: "Beschrijving", icon: "document-text" },
                      { value: "event", label: "Titel", icon: "text" },
                      { value: "all", label: "Alles", icon: "eye-off" },
                    ].map((option) => (
                      <TouchableOpacity
                        key={option.value}
                        onPress={() => setRevealTarget(option.value as typeof revealTarget)}
                        className="mr-2 mb-2 px-4 py-2 rounded-full flex-row items-center"
                        style={{
                          backgroundColor: revealTarget === option.value ? colors.primary : colors.surfaceSecondary,
                        }}
                      >
                        <Ionicons
                          name={option.icon as any}
                          size={16}
                          color={revealTarget === option.value ? "#FFFFFF" : colors.textSecondary}
                        />
                        <Text
                          className="ml-2 text-sm font-medium"
                          style={{ color: revealTarget === option.value ? "#FFFFFF" : colors.text }}
                        >
                          {option.label}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>

                  {/* Secret title (when hiding title) */}
                  {(revealTarget === "event" || revealTarget === "all") && (
                    <View className="mb-4">
                      <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                        Vervangende titel (zichtbaar voor anderen)
                      </Text>
                      <TextInput
                        value={secretTitle}
                        onChangeText={setSecretTitle}
                        placeholder="Bijv. 'Geheime verrassing...'"
                        placeholderTextColor={colors.textTertiary}
                        className="px-4 py-3 rounded-xl"
                        style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
                      />
                    </View>
                  )}

                  {/* Secret description (when hiding details) */}
                  {(revealTarget === "details" || revealTarget === "all") && (
                    <View className="mb-4">
                      <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                        Vervangende beschrijving
                      </Text>
                      <TextInput
                        value={secretDescription}
                        onChangeText={setSecretDescription}
                        placeholder="Bijv. 'Dit wordt later onthuld!'"
                        placeholderTextColor={colors.textTertiary}
                        className="px-4 py-3 rounded-xl"
                        style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
                        multiline
                        numberOfLines={2}
                      />
                    </View>
                  )}

                  {/* Reveal date/time */}
                  <View className="mb-4">
                    <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                      Onthullingsdatum (optioneel)
                    </Text>
                    <TouchableOpacity
                      onPress={() => setShowRevealDatePicker(true)}
                      className="flex-row items-center px-4 py-3 rounded-xl"
                      style={{ backgroundColor: colors.surfaceSecondary }}
                    >
                      <Ionicons name="time" size={20} color={colors.primary} />
                      <Text className="ml-3 flex-1" style={{ color: revealAt ? colors.text : colors.textTertiary }}>
                        {revealAt ? format(revealAt, "d MMM yyyy HH:mm", { locale: nl }) : "Geen datum ingesteld"}
                      </Text>
                      {revealAt && (
                        <TouchableOpacity onPress={() => setRevealAt(null)}>
                          <Ionicons name="close-circle" size={20} color={colors.textSecondary} />
                        </TouchableOpacity>
                      )}
                    </TouchableOpacity>
                    <Text className="text-xs mt-1" style={{ color: colors.textTertiary }}>
                      Kies wanneer de onthulling automatisch plaatsvindt
                    </Text>
                  </View>

                  {/* Linked Challenges */}
                  <View className="mb-4">
                    <View className="flex-row items-center justify-between mb-2">
                      <Text className="text-sm font-medium" style={{ color: colors.textSecondary }}>
                        Gekoppelde uitdagingen
                      </Text>
                      <TouchableOpacity
                        onPress={() => setShowChallengePicker(true)}
                        className="flex-row items-center px-3 py-1 rounded-full"
                        style={{ backgroundColor: colors.primary }}
                      >
                        <Ionicons name="add" size={16} color="#FFFFFF" />
                        <Text className="ml-1 text-sm font-medium" style={{ color: "#FFFFFF" }}>
                          Koppelen
                        </Text>
                      </TouchableOpacity>
                    </View>

                    {linkedChallenges.length === 0 ? (
                      <View
                        className="p-4 rounded-xl items-center"
                        style={{ backgroundColor: colors.surfaceSecondary }}
                      >
                        <Ionicons name="flash-outline" size={24} color={colors.textTertiary} />
                        <Text className="text-sm mt-2 text-center" style={{ color: colors.textTertiary }}>
                          Geen uitdagingen gekoppeld
                        </Text>
                        <Text className="text-xs mt-1 text-center" style={{ color: colors.textTertiary }}>
                          Koppel een uitdaging om de onthulling interactief te maken
                        </Text>
                      </View>
                    ) : (
                      linkedChallenges.map((item, index) => (
                        <View
                          key={item.challenge.id}
                          className="flex-row items-center justify-between p-3 rounded-xl mb-2"
                          style={{ backgroundColor: colors.surfaceSecondary }}
                        >
                          <View className="flex-1">
                            <Text className="font-medium" style={{ color: colors.text }}>
                              {item.challenge.title}
                            </Text>
                            <Text className="text-xs" style={{ color: colors.textSecondary }}>
                              Onthult: {item.revealTargets.join(", ")}
                            </Text>
                          </View>
                          <TouchableOpacity
                            onPress={() => {
                              setLinkedChallenges((prev) => prev.filter((_, i) => i !== index));
                            }}
                          >
                            <Ionicons name="close-circle" size={24} color={colors.error} />
                          </TouchableOpacity>
                        </View>
                      ))
                    )}
                  </View>

                  {/* Info box */}
                  <View
                    className="p-4 rounded-xl"
                    style={{ backgroundColor: colors.primaryLight }}
                  >
                    <Text className="text-sm font-medium" style={{ color: colors.primary }}>
                      💡 Na opslaan kun je ook hints toevoegen!
                    </Text>
                  </View>
                </View>
              )}
            </>
          )}

          {/* Bottom padding */}
          <View className="h-8" />
        </ScrollView>

        {/* Date Pickers (iOS) */}
        {Platform.OS === "ios" && showStartPicker && (
          <Modal transparent animationType="fade">
            <View className="flex-1 justify-end" style={{ backgroundColor: colors.overlay }}>
              <View
                className="rounded-t-2xl p-4"
                style={{ backgroundColor: colors.surface }}
              >
                <View className="flex-row justify-between mb-4">
                  <TouchableOpacity onPress={() => setShowStartPicker(false)}>
                    <Text style={{ color: colors.primary }}>Annuleer</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setShowStartPicker(false)}>
                    <Text className="font-semibold" style={{ color: colors.primary }}>
                      Klaar
                    </Text>
                  </TouchableOpacity>
                </View>
                <DateTimePicker
                  value={startDate}
                  mode={pickerMode}
                  display="spinner"
                  onChange={handleStartDateChange}
                  locale="nl-NL"
                  is24Hour={true}
                />
              </View>
            </View>
          </Modal>
        )}

        {Platform.OS === "ios" && showEndPicker && (
          <Modal transparent animationType="fade">
            <View className="flex-1 justify-end" style={{ backgroundColor: colors.overlay }}>
              <View
                className="rounded-t-2xl p-4"
                style={{ backgroundColor: colors.surface }}
              >
                <View className="flex-row justify-between mb-4">
                  <TouchableOpacity onPress={() => setShowEndPicker(false)}>
                    <Text style={{ color: colors.primary }}>Annuleer</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setShowEndPicker(false)}>
                    <Text className="font-semibold" style={{ color: colors.primary }}>
                      Klaar
                    </Text>
                  </TouchableOpacity>
                </View>
                <DateTimePicker
                  value={endDate}
                  mode={pickerMode}
                  display="spinner"
                  onChange={handleEndDateChange}
                  minimumDate={startDate}
                  locale="nl-NL"
                  is24Hour={true}
                />
              </View>
            </View>
          </Modal>
        )}

        {/* Android date pickers */}
        {Platform.OS === "android" && showStartPicker && (
          <DateTimePicker
            value={startDate}
            mode={pickerMode}
            display="default"
            onChange={handleStartDateChange}
            is24Hour={true}
          />
        )}

        {Platform.OS === "android" && showEndPicker && (
          <DateTimePicker
            value={endDate}
            mode={pickerMode}
            display="default"
            onChange={handleEndDateChange}
            minimumDate={startDate}
            is24Hour={true}
          />
        )}

        {/* Reveal Date Picker (iOS) */}
        {Platform.OS === "ios" && showRevealDatePicker && (
          <Modal transparent animationType="fade">
            <View className="flex-1 justify-end" style={{ backgroundColor: colors.overlay }}>
              <View
                className="rounded-t-2xl p-4"
                style={{ backgroundColor: colors.surface }}
              >
                <View className="flex-row justify-between mb-4">
                  <TouchableOpacity onPress={() => setShowRevealDatePicker(false)}>
                    <Text style={{ color: colors.primary }}>Annuleer</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setShowRevealDatePicker(false)}>
                    <Text className="font-semibold" style={{ color: colors.primary }}>
                      Klaar
                    </Text>
                  </TouchableOpacity>
                </View>
                <DateTimePicker
                  value={revealAt || new Date()}
                  mode="datetime"
                  display="spinner"
                  onChange={(event, date) => {
                    if (date) setRevealAt(date);
                  }}
                  minimumDate={new Date()}
                  locale="nl-NL"
                  is24Hour={true}
                />
              </View>
            </View>
          </Modal>
        )}

        {/* Reveal Date Picker (Android) */}
        {Platform.OS === "android" && showRevealDatePicker && (
          <DateTimePicker
            value={revealAt || new Date()}
            mode="datetime"
            display="default"
            onChange={(event, date) => {
              setShowRevealDatePicker(false);
              if (date) setRevealAt(date);
            }}
            minimumDate={new Date()}
            is24Hour={true}
          />
        )}

        {/* Challenge Picker Modal */}
        <ChallengePicker
          visible={showChallengePicker}
          onClose={() => setShowChallengePicker(false)}
          onSelect={(challenge, revealTargets) => {
            // Add to linked challenges (avoid duplicates)
            setLinkedChallenges((prev) => {
              if (prev.some((item) => item.challenge.id === challenge.id)) {
                return prev;
              }
              return [...prev, { challenge, revealTargets }];
            });
          }}
          unlinkedOnly={true}
        />
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );
};

export default CreateEventModal;

